<template>
  <div v-if="entries.length" class="pce">
    <div class="pce__head">
      <v-icon small class="mr-1">mdi-chart-timeline-variant</v-icon>
      <b>Preisänderungen</b>
      <span class="pce__hint">Je Land: alle geänderten Varianten zusammen, Kontrolle = unveränderte Varianten im selben Land<template v-if="doc.dailySyncedAt"> · Tageswerte Stand {{ formatDate(doc.dailySyncedAt) }}</template></span>
      <v-spacer />
      <div class="pce__win">
        <button v-for="n in [7, 14, 30]" :key="n" type="button" class="pce__winBtn" :class="{ 'pce__winBtn--on': windowDays === n }" @click="windowDays = n">{{ n }} Tage</button>
      </div>
    </div>

    <div v-for="g in countryGroups" :key="g.cc" class="pce__country" :class="{ 'pce__country--open': expanded[g.cc] }">
      <!-- Kopfzeile: Land, Umfang, Kennzahlen -->
      <button type="button" class="pce__countryHead" @click="toggle(g.cc)">
        <span class="pce__ccName">{{ flag(g.cc) }} {{ g.cc }}</span>
        <span class="pce__ccMeta">{{ g.changes.length }} {{ g.changes.length === 1 ? "Variante" : "Varianten" }} geändert · zuletzt {{ formatDate(g.latestAt) }}</span>
        <template v-if="!dailyLoaded[g.cc]"><span class="pce__muted">lädt …</span></template>
        <template v-else-if="countryEffect(g).tooEarly">
          <span class="pce__chip pce__chip--muted">noch zu früh · Tag {{ countryEffect(g).afterDays }} von 7<template v-if="countryEffect(g).after.units"> · bisher {{ countryEffect(g).after.units }} Stück</template></span>
        </template>
        <template v-else>
          <span class="pce__chip" :class="countryEffect(g).adjusted >= 0 ? 'pce__chip--pos' : 'pce__chip--neg'">Stück bereinigt {{ fmtPct(countryEffect(g).adjusted) }}</span>
          <span class="pce__chip" :class="pct(countryEffect(g).before.dbPerDay, countryEffect(g).after.dbPerDay) >= 0 ? 'pce__chip--pos' : 'pce__chip--neg'">DB/Tag {{ fmtPct(pct(countryEffect(g).before.dbPerDay, countryEffect(g).after.dbPerDay)) }}</span>
          <span class="pce__chip pce__chip--muted">Umsatz/Tag {{ fmtPct(pct(countryEffect(g).before.revPerDay, countryEffect(g).after.revPerDay)) }}</span>
        </template>
        <v-spacer />
        <v-icon small>{{ expanded[g.cc] ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </button>

      <!-- Zusammenfassung Land -->
      <div v-if="dailyLoaded[g.cc] && !countryEffect(g).tooEarly" class="pce__summary">
        <table class="pce__table">
          <thead><tr><th></th><th>vorher / Tag</th><th>nachher / Tag</th><th>Δ</th></tr></thead>
          <tbody>
            <tr><td>Stück (geänderte Varianten)</td><td>{{ n1(countryEffect(g).before.unitsPerDay) }}</td><td>{{ n1(countryEffect(g).after.unitsPerDay) }}</td><td :class="cls(pct(countryEffect(g).before.unitsPerDay, countryEffect(g).after.unitsPerDay))">{{ fmtPct(pct(countryEffect(g).before.unitsPerDay, countryEffect(g).after.unitsPerDay)) }}</td></tr>
            <tr><td>Umsatz</td><td>{{ money(countryEffect(g).before.revPerDay) }}</td><td>{{ money(countryEffect(g).after.revPerDay) }}</td><td :class="cls(pct(countryEffect(g).before.revPerDay, countryEffect(g).after.revPerDay))">{{ fmtPct(pct(countryEffect(g).before.revPerDay, countryEffect(g).after.revPerDay)) }}</td></tr>
            <tr><td>Deckungsbeitrag</td><td>{{ money(countryEffect(g).before.dbPerDay) }}</td><td>{{ money(countryEffect(g).after.dbPerDay) }}</td><td :class="cls(pct(countryEffect(g).before.dbPerDay, countryEffect(g).after.dbPerDay))">{{ fmtPct(pct(countryEffect(g).before.dbPerDay, countryEffect(g).after.dbPerDay)) }}</td></tr>
            <tr><td>Anteil an allen Verkäufen im Land</td><td>{{ n1(countryEffect(g).before.share) }} %</td><td>{{ n1(countryEffect(g).after.share) }} %</td><td :class="cls(countryEffect(g).after.share - countryEffect(g).before.share)">{{ fmtPts(countryEffect(g).after.share - countryEffect(g).before.share) }}</td></tr>
            <tr class="pce__control"><td>Kontrolle: unveränderte Varianten, Stück</td><td>{{ n1(countryEffect(g).control.before) }}</td><td>{{ n1(countryEffect(g).control.after) }}</td><td>{{ fmtPct(pct(countryEffect(g).control.before, countryEffect(g).control.after)) }}</td></tr>
          </tbody>
        </table>
        <div class="pce__spark" :title="'Stück je Tag (geänderte Varianten), ' + windowDays + ' Tage vor und nach der letzten Änderung'">
          <span v-for="d in countryEffect(g).bars" :key="d.day" class="pce__bar" :class="{ 'pce__bar--after': d.after, 'pce__bar--day': d.change }" :style="{ height: d.h + '%' }" :title="d.day + ': ' + d.u + ' Stück'"></span>
        </div>
      </div>

      <!-- Aufsplittung je Variante (aufgeklappt) -->
      <div v-if="expanded[g.cc]" class="pce__detail">
        <div v-for="c in g.changes" :key="c.key" class="pce__change">
          <div class="pce__changeHead">
            <b>{{ variantName(c.printVariantKey) }}</b>
            <span class="pce__price">{{ c.oldPrice.toFixed(2) }} → <b>{{ c.newPrice.toFixed(2) }} {{ c.currency }}</b></span>
            <span class="pce__target">{{ c.target === "priceList" ? `Preisliste ${c.market}` : `Basispreis ${c.shopKey}` }} · {{ formatDate(c.at) }} · {{ c.byName }}</span>
          </div>
          <div v-if="!dailyLoaded[g.cc]" class="pce__muted">Tageswerte werden geladen …</div>
          <div v-else-if="effect(c, g.cc).tooEarly" class="pce__muted">Noch zu früh: Tag {{ effect(c, g.cc).afterDays }} von 7<template v-if="effect(c, g.cc).after.units"> · bisher {{ effect(c, g.cc).after.units }} Stück</template></div>
          <template v-else>
            <table class="pce__table pce__table--small">
              <thead><tr><th></th><th>vorher / Tag</th><th>nachher / Tag</th><th>Δ</th></tr></thead>
              <tbody>
                <tr><td>Stück</td><td>{{ n1(effect(c, g.cc).before.unitsPerDay) }}</td><td>{{ n1(effect(c, g.cc).after.unitsPerDay) }}</td><td :class="cls(pct(effect(c, g.cc).before.unitsPerDay, effect(c, g.cc).after.unitsPerDay))">{{ fmtPct(pct(effect(c, g.cc).before.unitsPerDay, effect(c, g.cc).after.unitsPerDay)) }}</td></tr>
                <tr><td>Umsatz</td><td>{{ money(effect(c, g.cc).before.revPerDay) }}</td><td>{{ money(effect(c, g.cc).after.revPerDay) }}</td><td :class="cls(pct(effect(c, g.cc).before.revPerDay, effect(c, g.cc).after.revPerDay))">{{ fmtPct(pct(effect(c, g.cc).before.revPerDay, effect(c, g.cc).after.revPerDay)) }}</td></tr>
                <tr><td>Deckungsbeitrag</td><td>{{ money(effect(c, g.cc).before.dbPerDay) }}</td><td>{{ money(effect(c, g.cc).after.dbPerDay) }}</td><td :class="cls(pct(effect(c, g.cc).before.dbPerDay, effect(c, g.cc).after.dbPerDay))">{{ fmtPct(pct(effect(c, g.cc).before.dbPerDay, effect(c, g.cc).after.dbPerDay)) }}</td></tr>
                <tr><td>Anteil im Land</td><td>{{ n1(effect(c, g.cc).before.share) }} %</td><td>{{ n1(effect(c, g.cc).after.share) }} %</td><td :class="cls(effect(c, g.cc).after.share - effect(c, g.cc).before.share)">{{ fmtPts(effect(c, g.cc).after.share - effect(c, g.cc).before.share) }}</td></tr>
                <tr class="pce__control"><td>Bereinigt (minus Kontrolle)</td><td colspan="2"></td><td :class="cls(effect(c, g.cc).adjusted)">{{ fmtPct(effect(c, g.cc).adjusted) }}</td></tr>
              </tbody>
            </table>
            <div class="pce__spark pce__spark--small">
              <span v-for="d in effect(c, g.cc).bars" :key="d.day" class="pce__bar" :class="{ 'pce__bar--after': d.after, 'pce__bar--day': d.change }" :style="{ height: d.h + '%' }" :title="d.day + ': ' + d.u + ' Stück'"></span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template></span>
      <v-spacer />
      <div class="pce__win">
        <button v-for="n in [7, 14, 30]" :key="n" type="button" class="pce__winBtn" :class="{ 'pce__winBtn--on': windowDays === n }" @click="windowDays = n">{{ n }} Tage</button>
      </div>
    </div>

    <div v-for="e in entries" :key="e.id" class="pce__entry">
      <div class="pce__entryHead">
        <span class="pce__date">{{ formatDate(e.at) }}</span>
        <span class="pce__by">{{ e.by && (e.by.name || e.by.uid) }}</span>
      </div>
      <div v-for="c in e.changes" :key="c.key" class="pce__change">
        <div class="pce__changeHead">
          <b>{{ variantName(c.printVariantKey) }}</b>
          <span class="pce__price">{{ c.oldPrice.toFixed(2) }} → <b>{{ c.newPrice.toFixed(2) }} {{ c.currency }}</b></span>
          <span class="pce__target">{{ c.target === "priceList" ? `Preisliste ${c.market}` : `Basispreis ${c.shopKey}` }}</span>
          <span class="pce__countries">{{ (c.countries || []).join(", ") }}</span>
        </div>
        <div v-for="cc in analysisCountries(c)" :key="cc" class="pce__cc">
          <div class="pce__ccHead">{{ flag(cc) }} {{ cc }}</div>
          <div v-if="!dailyLoaded[cc]" class="pce__muted">Tageswerte werden geladen …</div>
          <template v-else>
            <div v-if="effect(c, cc).tooEarly" class="pce__muted">
              Noch zu früh: {{ effect(c, cc).afterDays }} von 7 Tagen nach der Änderung
              <span v-if="effect(c, cc).afterDays > 0">· bisher {{ effect(c, cc).after.units }} Stück</span>
            </div>
            <template v-else>
              <table class="pce__table">
                <thead>
                  <tr><th></th><th>vorher / Tag</th><th>nachher / Tag</th><th>Δ</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Stück</td><td>{{ n1(effect(c, cc).before.unitsPerDay) }}</td><td>{{ n1(effect(c, cc).after.unitsPerDay) }}</td>
                    <td :class="pct(effect(c, cc).before.unitsPerDay, effect(c, cc).after.unitsPerDay) >= 0 ? 'pce__pos' : 'pce__neg'">{{ fmtPct(pct(effect(c, cc).before.unitsPerDay, effect(c, cc).after.unitsPerDay)) }}</td>
                  </tr>
                  <tr>
                    <td>Umsatz</td><td>{{ money(effect(c, cc).before.revPerDay) }}</td><td>{{ money(effect(c, cc).after.revPerDay) }}</td>
                    <td :class="pct(effect(c, cc).before.revPerDay, effect(c, cc).after.revPerDay) >= 0 ? 'pce__pos' : 'pce__neg'">{{ fmtPct(pct(effect(c, cc).before.revPerDay, effect(c, cc).after.revPerDay)) }}</td>
                  </tr>
                  <tr>
                    <td>Deckungsbeitrag</td><td>{{ money(effect(c, cc).before.dbPerDay) }}</td><td>{{ money(effect(c, cc).after.dbPerDay) }}</td>
                    <td :class="pct(effect(c, cc).before.dbPerDay, effect(c, cc).after.dbPerDay) >= 0 ? 'pce__pos' : 'pce__neg'">{{ fmtPct(pct(effect(c, cc).before.dbPerDay, effect(c, cc).after.dbPerDay)) }}</td>
                  </tr>
                  <tr>
                    <td>Anteil an allen Verkäufen</td><td>{{ n1(effect(c, cc).before.share) }} %</td><td>{{ n1(effect(c, cc).after.share) }} %</td>
                    <td :class="effect(c, cc).after.share - effect(c, cc).before.share >= 0 ? 'pce__pos' : 'pce__neg'">{{ fmtPts(effect(c, cc).after.share - effect(c, cc).before.share) }}</td>
                  </tr>
                  <tr class="pce__control">
                    <td>Kontrolle: andere Varianten, Stück</td><td>{{ n1(effect(c, cc).control.before) }}</td><td>{{ n1(effect(c, cc).control.after) }}</td>
                    <td>{{ fmtPct(pct(effect(c, cc).control.before, effect(c, cc).control.after)) }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="pce__verdict">
                <b>Bereinigter Effekt auf die Stückzahl: <span :class="effect(c, cc).adjusted >= 0 ? 'pce__pos' : 'pce__neg'">{{ fmtPct(effect(c, cc).adjusted) }}</span></b>
                <span class="pce__hint">= Δ Stück der Variante minus Δ Stück der Kontrolle · Deckungsbeitrag/Tag {{ fmtPct(pct(effect(c, cc).before.dbPerDay, effect(c, cc).after.dbPerDay)) }}</span>
              </div>
              <div class="pce__spark" :title="'Stück je Tag, ' + windowDays + ' Tage vor und nach der Änderung'">
                <span v-for="d in effect(c, cc).bars" :key="d.day" class="pce__bar" :class="{ 'pce__bar--after': d.after, 'pce__bar--day': d.change }" :style="{ height: d.h + '%' }" :title="d.day + ': ' + d.u + ' Stück'"></span>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../../fb";
import { costsFor, marginFor } from "./pricing";

const DAY = 86400000;
const dayKey = (ms) => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ms));

/** Vorher-Nachher-Auswertung der protokollierten Preisänderungen (applyLog) auf Basis der Tageswerte je Land */
export default {
  name: "PriceChangeEffects",
  props: {
    doc: { type: Object, default: () => ({}) },
    printVariants: { type: Array, default: () => [] },
    config: { type: Object, required: true },
    employeeByVariant: { type: Object, default: () => ({}) },
  },
  data() {
    return { windowDays: 14, dailyLoaded: {}, daily: {}, expanded: {} };
  },
  computed: {
    entries() {
      const log = Array.isArray(this.doc.applyLog) ? this.doc.applyLog : [];
      return [...log]
        .sort((a, b) => b.at - a.at)
        .slice(0, 20)
        .map((e, i) => ({ ...e, id: `${e.at}-${i}`, changes: (e.changes || []).map((c, j) => ({ ...c, key: `${e.at}-${j}`, at: e.at, byName: (e.by && (e.by.name || e.by.uid)) || "" })) }));
    },
    /** Änderungen nach Land gruppiert (eine Änderung kann mehrere Länder betreffen, z. B. Basispreis) */
    countryGroups() {
      const sold = this.doc.countries || {};
      const groups = {};
      for (const e of this.entries) {
        for (const c of e.changes) {
          for (const cc of this.analysisCountries(c)) {
            const g = (groups[cc] ||= { cc, changes: [], latestAt: 0, pvs: new Set() });
            g.changes.push({ ...c, key: `${c.key}-${cc}` });
            g.latestAt = Math.max(g.latestAt, c.at);
            g.pvs.add(c.printVariantKey);
          }
        }
      }
      return Object.values(groups).sort((a, b) => (sold[b.cc] || 0) - (sold[a.cc] || 0));
    },
  },
  watch: {
    entries: { immediate: true, handler: "ensureDaily" },
  },
  methods: {
    toggle(cc) {
      this.$set(this.expanded, cc, !this.expanded[cc]);
    },
    cls(v) {
      return v >= 0 ? "pce__pos" : "pce__neg";
    },
    /** Zusammenfassung je Land: alle geänderten Varianten um die letzte Änderung, Kontrolle = unveränderte Varianten */
    countryEffect(g) {
      const cc = g.cc;
      const byPv = this.daily[cc] || {};
      const N = this.windowDays;
      const at = g.latestAt;
      const changeDay = dayKey(at);
      const todayKey = dayKey(Date.now());
      const dayOf = (offset) => dayKey(at + offset * DAY);
      const before = [];
      const after = [];
      for (let i = N; i >= 1; i--) before.push(dayOf(-i));
      for (let i = 1; i <= N; i++) { const k = dayOf(i); if (k < todayKey) after.push(k); }
      const sum = (pv, days) => days.reduce((acc, k) => { const v = (byPv[pv] || {})[k]; return { u: acc.u + (v ? v.u : 0), g: acc.g + (v ? v.g : 0) }; }, { u: 0, g: 0 });
      const kpi = (days) => {
        const n = Math.max(1, days.length);
        let cu = 0, cg = 0, db = 0, ou = 0;
        for (const pv of Object.keys(byPv)) {
          const s = sum(pv, days);
          if (g.pvs.has(pv)) {
            cu += s.u; cg += s.g;
            if (s.u) {
              const variant = this.printVariants.find((v) => v.key === pv) || {};
              const m = marginFor(s.g / s.u, cc, this.config, costsFor(variant, cc), this.employeeByVariant[pv] || 0);
              db += (m.margin || 0) * s.u;
            }
          } else ou += s.u;
        }
        return { units: cu, unitsPerDay: cu / n, revPerDay: cg / n, dbPerDay: db / n, share: cu + ou ? (cu / (cu + ou)) * 100 : 0, othersPerDay: ou / n };
      };
      const b = kpi(before);
      const a = kpi(after);
      const control = { before: b.othersPerDay, after: a.othersPerDay };
      const adjusted = this.pct(b.unitsPerDay, a.unitsPerDay) - this.pct(control.before, control.after);
      const seq = [...before, changeDay, ...after];
      const units = seq.map((k) => [...g.pvs].reduce((t, pv) => t + (((byPv[pv] || {})[k] || { u: 0 }).u), 0));
      const max = Math.max(1, ...units);
      const bars = seq.map((k, i) => ({ day: k, u: units[i], h: Math.round((units[i] / max) * 100), after: k > changeDay, change: k === changeDay }));
      return { tooEarly: after.length < 7, afterDays: after.length, before: b, after: a, control, adjusted, bars };
    },
    variantName(key) {
      const v = this.printVariants.find((x) => x.key === key);
      return v ? v.name : key;
    },
    flag(cc) {
      return /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : "";
    },
    formatDate(ms) {
      return new Date(ms).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    money(v) {
      return `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
    },
    n1(v) {
      return Number(v || 0).toFixed(1).replace(".", ",");
    },
    pct(a, b) {
      return a > 0 ? ((b - a) / a) * 100 : b > 0 ? 100 : 0;
    },
    fmtPct(p) {
      return `${p >= 0 ? "+" : ""}${Number(p).toFixed(0)} %`;
    },
    fmtPts(p) {
      return `${p >= 0 ? "+" : ""}${Number(p).toFixed(1).replace(".", ",")} Pkt.`;
    },
    /** Länder, für die der Effekt gezeigt wird: Preisliste → Marktland, Basispreis → alle betroffenen Länder mit Verkäufen */
    analysisCountries(c) {
      const sold = this.doc.countries || {};
      const list = c.countries && c.countries.length ? c.countries : c.cc ? [c.cc] : [];
      return list.filter((cc) => sold[cc]).sort((a, b) => (sold[b] || 0) - (sold[a] || 0));
    },
    async ensureDaily() {
      const need = new Set();
      for (const e of this.entries) for (const c of e.changes) for (const cc of this.analysisCountries(c)) need.add(cc);
      for (const cc of need) {
        if (this.dailyLoaded[cc]) continue;
        const snap = await getDoc(doc(db, "settings", "printVariantPrices", "daily", cc));
        this.$set(this.daily, cc, snap.exists() ? snap.data().byVariant || {} : {});
        this.$set(this.dailyLoaded, cc, true);
      }
    },
    /** Kennzahlen vorher/nachher für eine Änderung in einem Land */
    effect(c, cc) {
      const byPv = this.daily[cc] || {};
      const N = this.windowDays;
      const changeDay = dayKey(c.at || 0);
      const todayKey = dayKey(Date.now());
      const dayOf = (offset) => dayKey((c.at || 0) + offset * DAY);
      const before = [];
      const after = [];
      for (let i = N; i >= 1; i--) before.push(dayOf(-i));
      for (let i = 1; i <= N; i++) {
        const k = dayOf(i);
        if (k < todayKey) after.push(k);
      }
      const sum = (pv, days) => days.reduce((acc, k) => { const v = (byPv[pv] || {})[k]; return { u: acc.u + (v ? v.u : 0), g: acc.g + (v ? v.g : 0) }; }, { u: 0, g: 0 });
      const sumAll = (days, exceptPv) => Object.keys(byPv).filter((pv) => pv !== exceptPv).reduce((acc, pv) => { const s = sum(pv, days); return { u: acc.u + s.u, g: acc.g + s.g }; }, { u: 0, g: 0 });
      const variant = this.printVariants.find((v) => v.key === c.printVariantKey) || {};
      const costs = costsFor(variant, cc);
      const employee = this.employeeByVariant[c.printVariantKey] || 0;
      const kpi = (days) => {
        const s = sum(c.printVariantKey, days);
        const others = sumAll(days, c.printVariantKey);
        const n = Math.max(1, days.length);
        const grossUnit = s.u ? s.g / s.u : null;
        const m = grossUnit !== null ? marginFor(grossUnit, cc, this.config, costs, employee) : { margin: 0 };
        return { units: s.u, unitsPerDay: s.u / n, revPerDay: s.g / n, dbPerDay: ((m.margin || 0) * s.u) / n, share: s.u + others.u ? (s.u / (s.u + others.u)) * 100 : 0, othersPerDay: others.u / n };
      };
      const b = kpi(before);
      const a = kpi(after);
      const control = { before: b.othersPerDay, after: a.othersPerDay };
      const adjusted = this.pct(b.unitsPerDay, a.unitsPerDay) - this.pct(control.before, control.after);
      const seq = [...before, changeDay, ...after];
      const units = seq.map((k) => ((byPv[c.printVariantKey] || {})[k] || { u: 0 }).u);
      const max = Math.max(1, ...units);
      const bars = seq.map((k, i) => ({ day: k, u: units[i], h: Math.round((units[i] / max) * 100), after: k > changeDay, change: k === changeDay }));
      return { tooEarly: after.length < 7, afterDays: after.length, before: b, after: a, control, adjusted, bars };
    },
  },
};
</script>

<style lang="scss" scoped>
.pce {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f6f7f9;

  &__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
    margin-bottom: 10px;
  }
  &__hint {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__win {
    display: inline-flex;
    gap: 2px;
    padding: 2px;
    border-radius: 999px;
    background: #e6e8ee;
  }
  &__winBtn {
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;

    &--on {
      background: #fff;
      color: #282828;
    }
  }
  &__country {
    border-radius: 12px;
    background: #fff;
    margin-bottom: 8px;
    overflow: hidden;
  }
  &__countryHead {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
    padding: 10px 14px;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    font: inherit;
  }
  &__ccName {
    font-size: 14px;
    font-weight: 800;
  }
  &__ccMeta {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__chip {
    font-size: 11.5px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 999px;
    background: #eef0f4;
    color: rgba(0, 0, 0, 0.65);

    &--pos {
      background: #e3f3ea;
      color: #2f7d52;
    }
    &--neg {
      background: #fbe4e4;
      color: #c62828;
    }
    &--muted {
      background: #eef0f4;
      color: rgba(0, 0, 0, 0.55);
      font-weight: 600;
    }
  }
  &__summary {
    padding: 0 14px 12px;
  }
  &__detail {
    padding: 4px 14px 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #fafbfc;
  }
  &__table--small {
    max-width: 480px;
    font-size: 12px;
  }
  &__spark--small {
    height: 24px;
    max-width: 480px;
  }
  &__entry {
    padding: 10px 12px;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 8px;
  }
  &__entryHead {
    display: flex;
    gap: 10px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    margin-bottom: 6px;
  }
  &__date {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.75);
  }
  &__change {
    padding-top: 6px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-top: 6px;
  }
  &__changeHead {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 10px;
    font-size: 13px;
  }
  &__target,
  &__countries {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__cc {
    margin-top: 8px;
    padding: 8px 10px;
    border-radius: 10px;
    background: #f6f7f9;
  }
  &__ccHead {
    font-size: 12.5px;
    font-weight: 700;
    margin-bottom: 4px;
  }
  &__muted {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__table {
    width: 100%;
    max-width: 560px;
    border-collapse: collapse;
    font-size: 12.5px;

    th,
    td {
      padding: 3px 8px;
      text-align: right;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }
    th:first-child,
    td:first-child {
      text-align: left;
      color: rgba(0, 0, 0, 0.65);
    }
    th {
      font-size: 11px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.45);
    }
  }
  &__control td {
    color: rgba(0, 0, 0, 0.45);
    font-style: italic;
  }
  &__verdict {
    margin-top: 6px;
    font-size: 12.5px;
  }
  &__pos {
    color: #2f7d52;
    font-weight: 700;
  }
  &__neg {
    color: #c62828;
    font-weight: 700;
  }
  &__spark {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 36px;
    margin-top: 8px;
    max-width: 560px;
  }
  &__bar {
    flex: 1;
    min-height: 2px;
    border-radius: 2px 2px 0 0;
    background: rgba(0, 0, 0, 0.2);

    &--after {
      background: #7b5cd6;
    }
    &--day {
      background: #c62828;
    }
  }
}
</style>
